import { Connect } from '@genesislcap/foundation-comms';
import { customElement, GenesisElement, observable } from '@genesislcap/web-core';
import { showNotificationToast } from '@genesislcap/foundation-notifications';
import { SimulatorStyles as styles } from './simulator.styles';
import { SimulatorTemplate as template } from './simulator.template';

@customElement({
  name: 'simulator-route',
  template,
  styles,
})
export class SimulatorRoute extends GenesisElement {
  @Connect connect!: Connect;
  @observable numberOfMessages = '1';
  @observable dueInMins = '60';
  @observable firmInMins = '120';
  @observable selectedPriceType = 'PERCENTAGE';
  @observable selectedCustomerType = '1';

  // Multi-select counterparty properties for QuoteRequests
  @observable targetCounterpartyOptions: any[] = [];
  @observable blockCounterpartyOptions: any[] = [];
  @observable selectedTargetCounterparties: string[] = [];
  @observable selectedBlockCounterparties: string[] = [];

  // QuoteStatusReport properties
  @observable reference = '';
  @observable counterpartyId = '';
  @observable quoteStatusText = '';
  @observable quoteStatusCounterpartyOptions: any[] = [];
  @observable selectedQuoteStatus = 'PASS';

  // ExecutionReport properties
  @observable executionReference = '';
  @observable executionCounterpartyId = '';
  @observable executionText = '';
  @observable executionCounterpartyOptions: any[] = [];

  async connectedCallback() {
    super.connectedCallback();
    await this.loadCounterparties();
  }

  private async loadCounterparties() {
    try {
      const response = await this.connect.request('REQ_COUNTERPARTY');
      const counterparties = response.REPLY || [];

      // For QuoteStatusReport - include "All Counterparties"
      this.quoteStatusCounterpartyOptions = [...counterparties];
      this.quoteStatusCounterpartyOptions.push({
        COUNTERPARTY_ID: 'ALL_COUNTERPARTIES'
      });
      this.counterpartyId = this.quoteStatusCounterpartyOptions[0].COUNTERPARTY_ID;

      // For ExecutionReport - exclude "All Counterparties"
      this.executionCounterpartyOptions = [...counterparties];
      this.executionCounterpartyId = this.executionCounterpartyOptions[0].COUNTERPARTY_ID;
      
      // For multi-select dropdowns - same as execution (without "All Counterparties")
      this.targetCounterpartyOptions = [...counterparties];
      this.blockCounterpartyOptions = [...counterparties];
    } catch (error) {
      console.error('Failed to load counterparties:', error);
      this.quoteStatusCounterpartyOptions = [];
      this.executionCounterpartyOptions = [];
      this.targetCounterpartyOptions = [];
      this.blockCounterpartyOptions = [];
    }
  }

  get targetCounterpartyDropdownItems() {
    return this.targetCounterpartyOptions.map((counterparty) => ({
      value: counterparty.COUNTERPARTY_ID,
      label: counterparty.COUNTERPARTY_ID,
    }));
  }

  get blockCounterpartyDropdownItems() {
    return this.blockCounterpartyOptions.map((counterparty) => ({
      value: counterparty.COUNTERPARTY_ID,
      label: counterparty.COUNTERPARTY_ID,
    }));
  }

  onTargetCounterpartySelectionChange(event: CustomEvent) {
    this.selectedTargetCounterparties = event.detail;
  }

  onBlockCounterpartySelectionChange(event: CustomEvent) {
    this.selectedBlockCounterparties = event.detail;
  }

  public async callEventSendQuoteRequests() {
    
    const response = await this.connect.commitEvent('EVENT_SEND_QUOTE_REQUESTS', {
      DETAILS: {
        NUMBER_OF_MESSAGES: parseInt(this.numberOfMessages) || 1,
        PRICE_TYPE: this.selectedPriceType,
        DUE_IN_MINS: parseFloat(this.dueInMins) || 60,
        FIRM_IN_MINS: parseFloat(this.firmInMins) || 120,
        CUSTOMER_TYPE: parseInt(this.selectedCustomerType),
        TARGET_COUNTERPARTIES: this.selectedTargetCounterparties,
        BLOCK_COUNTERPARTIES: this.selectedBlockCounterparties,
      },
    });

    if (response.MESSAGE_TYPE === 'EVENT_NACK') {
      // Show error toast for NACK
      showNotificationToast(
        {
          title: 'Quote Requests Failed',
          body: 'Failed to send quote requests. ' + response.ERROR[0].TEXT,
          toast: {
            autoClose: true,
            closeTimeout: 5000,
            type: 'error',
          },
        },
        'rapid',
      );
      console.error('Failed to send quote requests:', response.ERROR[0].TEXT);
    } else {
      // Show success toast for ACK
      showNotificationToast(
        {
          title: 'Quote Requests Sent',
          body: `Successfully sent ${this.numberOfMessages} quote request(s)`,
          toast: {
            autoClose: true,
            closeTimeout: 3000,
            type: 'success',
          },
        },
        'rapid',
      );
    }
  }

  public async callEventSendQuoteStatusReport() {
    const response = await this.connect.commitEvent('EVENT_SEND_QUOTE_STATUS_REPORT', {
      DETAILS: {
        BOND_ID: this.reference,
        COUNTERPARTY_ID: this.counterpartyId,
        QUOTE_STATUS: this.selectedQuoteStatus,
        TEXT: this.quoteStatusText,
      },
    });

    if (response.MESSAGE_TYPE === 'EVENT_NACK') {
      // Show error toast for NACK
      showNotificationToast(
        {
          title: 'Quote Status Report Failed',
          body: 'Failed to send quote status report. ' + response.ERROR[0].TEXT,
          toast: {
            autoClose: true,
            closeTimeout: 5000,
            type: 'error',
          },
        },
        'rapid',
      );
      console.error('Failed to send quote status report:', response.ERROR[0].TEXT);
    } else {
      // Show success toast for ACK
      showNotificationToast(
        {
          title: 'Execution Report Sent',
          body: `Successfully sent execution report for bond ${this.executionReference}`,
          toast: {
            autoClose: true,
            closeTimeout: 3000,
            type: 'success',
          },
        },
        'rapid',
      );
    }
  }

  public async callEventSendExecutionReport() {
    const response = await this.connect.commitEvent('EVENT_SEND_EXECUTION_REPORT', {
      DETAILS: {
        BOND_ID: this.executionReference,
        COUNTERPARTY_ID: this.executionCounterpartyId,
        TEXT: this.executionText,
      },
    });

    if (response.MESSAGE_TYPE === 'EVENT_NACK') {
      // Show error toast for NACK
      showNotificationToast(
        {
          title: 'Execution Report Failed',
          body: 'Failed to send execution report. ' + response.ERROR[0].TEXT,
          toast: {
            autoClose: true,
            closeTimeout: 5000,
            type: 'error',
          },
        },
        'rapid',
      );
      console.error('Failed to send execution report:', response.ERROR[0].TEXT);
    } else {
      // Show success toast for ACK
      showNotificationToast(
        {
          title: 'Execution Report Sent',
          body: `Successfully sent execution report for bond ${this.executionReference}`,
          toast: {
            autoClose: true,
            closeTimeout: 3000,
            type: 'success',
          },
        },
        'rapid',
      );
    }
  }
}
